// Site scan viewer (2026-10-15, reports/ar_scan_mode_spec_2026-10-14.md
// section 19): a Street View / Mapillary-like way to look at a site's scans,
// on the desktop and on the phone. Opened from the "Site scans" map layer
// (desktop: siteScansMap.ts; field app: fieldApp.ts) or from an asset's
// "As-built scans" list.
//
//  * Frames tab: the blurred frames full width, in capture order along the
//    path. Forward / back (buttons, arrow keys, swipe), a thumbnail strip
//    (lazy-loaded), a mini-map with the capture track, the current position
//    and its heading cone (click it to jump to the nearest frame), a visit
//    timeline (current / history / as-built), capture date and recorder.
//    Neighbouring frames are prefetched. WRAP assets near the frame are
//    labelled with arMath's projection from the frame's stored pose: marked
//    APPROXIMATE, as in the AR view.
//  * 3D model tab: "Not built yet: waiting for the scan toolkit" until the
//    scan adapter reports a model for the site; then a lightweight three.js
//    viewer (MIT, vendored under /vendor/three/, loaded only when the tab is
//    opened) of the GLB mesh or PLY point cloud, the recognised assets, and
//    a "show as-built / below-ground" toggle for the as-built layers.
//
// Only blurred data exists on the server: every image here (thumb, medium,
// full) is made from the stored blurred frame.
import maplibregl from "maplibre-gl";
import type { GeoJSONSource, Map as MlMap } from "maplibre-gl";

import { closeOnBack } from "./backClose";
import { cameraBasis, focalPx, projectPoint } from "./field/arMath";
import type { AreaAsset, AssetLabel, SiteFrame, SiteSummary, SiteVisit } from "./scanSitesCore";
import type { FramePose, FrameMove } from "./scanSitesCore";
import {
  assetLabels, enuOffset, frameBetaGamma, frameMove, hotspots, meanUp, headingCone, nearestFrame, prefetchOrder, shortDate, siteRadiusM, stepIndex, swipeStep,
  visitLabel,
} from "./scanSitesCore";
import "./styles/scanViewer.css";

const API = "/api/field/scans";
const MINI_STYLE = "https://tiles.openfreemap.org/styles/positron";
const THREE_BASE = "/vendor/three/";

interface SiteDetail extends SiteSummary {
  sessions: SiteVisit[];
  current_window_months: number;
  can_delete: boolean;
}

interface ModelStatus {
  status: "ready" | "not_built";
  reason?: string;
  detail?: string | null;
  file_url?: string;
  elements_url?: string | null;
  as_built_urls?: string[];
  model?: {
    format: "glb" | "ply";
    kind: string;
    revision: string;
    built_at: string;
    sample: boolean;
    fusion: string;
    frames_used: number | null;
    registration: { residual_m: number | null; n_pairs: number; rotation_deg: number; basis?: "walk" | "assets" | null; n_asset_pairs?: number | null } | null;
    as_built_layers: { session: string; format: "glb" | "ply"; captured_at: string | null; project_id: string | null }[];
  };
  inputs?: { current: { session: string }[]; history: { session: string }[]; as_built: { session: string }[] };
}

interface ElementRec {
  element_id: string;
  kind: string;
  centre_mm: [number, number, number];
  confidence: number | null;
  match: { asset_id: string } | null;
  review: boolean;
  jump_m: number | null;
  last_observed_at: string | null;
}

/** An asset a person named on a frame (2026-10-16): a point as fractions of the frame. */
interface SiteLabel {
  id: number;
  session: string;
  idx: number;
  x: number;
  y: number;
  text: string | null;
  asset_id: string | null;
  asset_type: string | null;
  source: string;
  mine: boolean;
}

interface ViewerState {
  site: SiteDetail;
  frames: SiteFrame[];
  /** items marked on the frames (type-only or a named asset, review #5); naming is unused since row #36 (a tap marks) */
  labels: SiteLabel[];
  naming: boolean;
  /** the visit filter: null = every visit in order */
  visit: string | null;
  i: number;
  assets: AreaAsset[];
  showAssets: boolean;
  tab: "frames" | "model";
  /** the site model's camera per frame ("session:idx"), when it has one (row #32: Street View-style moves) */
  poses: Map<string, FramePose>;
  poseFocal: { fx: number; fy: number } | null;
  /** the world's up in the poses' frame (the mean of the cameras' image-up), for the hotspots' ground points */
  poseUp: [number, number, number];
  /** auto-advance along the path */
  playing: boolean;
  /** the Frames tab's full screen (row #35): just the frame, full size, with close / minimise */
  fs: boolean;
  /** panoramas stitched where someone turned (row #38), and which one each frame ("session:idx") is in */
  panos: Pano[];
  panoOf: Map<string, Pano>;
}

interface Pano {
  id: string;
  session: string;
  idxs: number[];
  headings: number[];
  /** row #44: the frames in it from every visit, in heading order (unwrapped), and the strip's left edge heading */
  members?: { session: string; idx: number; heading: number }[];
  heading_left_deg?: number | null;
  /** the strip's width in degrees (row #44; before it, the turn) */
  span_deg: number;
  /** row #46: how well its frames line up (px at the working size) and how many of the turn's frames it uses */
  quality?: "good" | "fair" | "poor";
  alignment_px?: { median: number; max: number } | null;
  frames_kept?: number;
  frames_in?: number;
  hfov_deg: number;
  width: number;
  height: number;
  url: string;
}

/** The degrees of the full circle a panorama shows (its strip's width; older ones: the turn plus a frame). */
function panoDegrees(p: Pano): number {
  return Math.min(360, p.heading_left_deg !== undefined && p.heading_left_deg !== null ? p.span_deg : p.span_deg + p.hfov_deg);
}

/** Moves between frames (row #32). MOVE_MS: one step's blend; PLAY_MS: a step while playing. */
const MOVE_MS = 520;
const PLAY_MS = 900;
let playTimer: number | null = null;
let moveSeq = 0;
/** history entries the back gesture closes the viewer / its full screen by (backClose.ts) */
let releaseViewerBack: (() => void) | null = null;
/** what a person can mark (field_scans.ITEM_TYPES, from the labels API); this list until it loads */
let itemTypes: Record<string, string> = {
  backflow: "Backflow cage", manhole: "Manhole / sump cover", meter: "Meter box", valve: "Valve lid", toby: "Toby",
  hydrant: "Hydrant", kerb_inlet: "Kerb inlet / grate", other: "Other",
};
const OVERLAY_KEY = "wrap.scanViewer.overlay";

/** the overlay on/off choice, remembered per browser (row #36) */
function overlayPref(): boolean {
  try {
    return window.localStorage.getItem(OVERLAY_KEY) !== "off";
  } catch {
    return true;
  }
}

function setOverlay(on: boolean): void {
  if (!V) return;
  V.showAssets = on;
  try {
    window.localStorage.setItem(OVERLAY_KEY, on ? "on" : "off");
  } catch {
    // not remembered
  }
  root?.querySelectorAll<HTMLInputElement>(".ssv-toggle input").forEach((cb) => (cb.checked = on));
  const b = root?.querySelector<HTMLButtonElement>(".ssv-overlay");
  if (b) {
    b.classList.toggle("on", on);
    b.setAttribute("aria-pressed", String(on));
    b.title = on ? "Hide the labels" : "Show the labels";
  }
  renderLabels();
}
let releaseFsBack: (() => void) | null = null;

let root: HTMLDivElement | null = null;
let V: ViewerState | null = null;
let mini: MlMap | null = null;
let miniReady = false;
let keyHandler: ((e: KeyboardEvent) => void) | null = null;
let threeCleanup: (() => void) | null = null;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "same-origin" });
  if (!res.ok) {
    const d = await res.json().catch(() => null);
    throw new Error(d?.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export function frameUrl(f: Pick<SiteFrame, "session" | "idx" | "version">, size: "thumb" | "medium" | "full"): string {
  return `${API}/${encodeURIComponent(f.session)}/frames/${f.idx}?size=${size}&v=${f.version}`;
}

function visible(): SiteFrame[] {
  if (!V) return [];
  return V.visit ? V.frames.filter((f) => f.session === V!.visit) : V.frames;
}

function visitOf(session: string): SiteVisit | undefined {
  return V?.site.sessions.find((s) => s.client_uuid === session);
}

// --- open / close -------------------------------------------------------------

/** Open a site's viewer (optionally on one visit / frame). */
export async function openSiteViewer(siteId: number, opts: { session?: string; idx?: number; tab?: "frames" | "model" } = {}): Promise<void> {
  closeSiteViewer();
  root = el("div", "ssv");
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");
  root.setAttribute("aria-label", "Site scan viewer");
  root.append(el("div", "ssv-loading", "Loading the site's scans…"));
  document.body.append(root);
  releaseViewerBack = closeOnBack(() => {
    releaseViewerBack = null;
    closeSiteViewer();
  });
  keyHandler = onKey;
  document.addEventListener("keydown", keyHandler);
  try {
    const [site, fr] = await Promise.all([
      getJson<SiteDetail>(`${API}/sites/${siteId}`),
      getJson<{ frames: SiteFrame[] }>(`${API}/sites/${siteId}/frames`),
    ]);
    if (!root) return;
    V = { site, frames: fr.frames, labels: [], naming: false, visit: null, i: 0, assets: [], showAssets: overlayPref(), tab: opts.tab ?? "frames",
      poses: new Map(), poseFocal: null, poseUp: [0, 0, 1], playing: false, fs: false, panos: [], panoOf: new Map() };
    // default visit: the one asked for, else the newest current visit, else everything
    const newestCurrent = [...site.sessions].reverse().find((s) => s.role === "current" && s.n_frames > 0);
    V.visit = opts.session ?? newestCurrent?.client_uuid ?? null;
    const vis = visible();
    if (opts.idx !== undefined) {
      const k = vis.findIndex((f) => f.idx === opts.idx);
      if (k >= 0) V.i = k;
    }
    render();
    void loadAssets();
    void loadNames();
    void loadPoses();
    void loadPanoramas();
  } catch (err) {
    if (!root) return;
    root.replaceChildren(el("div", "ssv-error", `Couldn't open the site: ${(err as Error).message}`), closeButton());
  }
}

/** A link to a site: ?scan_site=<id>[&scan_session=<uuid>][&scan_view=0]
 * (scan_view=0 only moves the map there). Read once per page load. */
let urlUsed = false;
export function siteFromUrl(): { id: number; session?: string; view: boolean } | null {
  if (urlUsed) return null;
  urlUsed = true;
  try {
    const q = new URLSearchParams(window.location.search);
    const id = Number(q.get("scan_site"));
    if (!Number.isInteger(id) || id <= 0) return null;
    return { id, session: q.get("scan_session") ?? undefined, view: q.get("scan_view") !== "0" };
  } catch {
    return null;
  }
}

export function closeSiteViewer(): void {
  stopPlay();
  closePano();
  leaveFullScreen(false);
  releaseViewerBack?.();
  releaseViewerBack = null;
  if (keyHandler) document.removeEventListener("keydown", keyHandler);
  keyHandler = null;
  threeCleanup?.();
  threeCleanup = null;
  mini?.remove();
  mini = null;
  miniReady = false;
  root?.remove();
  root = null;
  V = null;
}

function closeButton(): HTMLButtonElement {
  const b = el("button", "ssv-close", "×");
  b.type = "button";
  b.title = "Close (Esc)";
  b.setAttribute("aria-label", "Close the viewer");
  b.addEventListener("click", closeSiteViewer);
  return b;
}

async function loadAssets(): Promise<void> {
  if (!V) return;
  const s = V.site;
  try {
    const r = Math.min(500, siteRadiusM(s));
    const d = await getJson<{ assets: AreaAsset[] }>(`/api/field/area?lat=${s.lat}&lon=${s.lon}&r=${r}`);
    if (!V) return;
    V.assets = d.assets ?? [];
    renderLabels();
  } catch {
    /* labels are optional */
  }
}

async function loadNames(): Promise<void> {
  if (!V) return;
  try {
    const d = await getJson<{ labels: SiteLabel[]; item_types?: Record<string, string> }>(`${API}/sites/${V.site.id}/labels`);
    if (!V) return;
    V.labels = d.labels ?? [];
    if (d.item_types && Object.keys(d.item_types).length) itemTypes = d.item_types;
    renderLabels();
    renderNamingBox();
  } catch {
    /* naming is optional */
  }
}

// --- render ---------------------------------------------------------------------

function render(): void {
  if (!root || !V) return;
  threeCleanup?.();
  threeCleanup = null;
  mini?.remove();
  mini = null;
  miniReady = false;
  const s = V.site;
  const head = el("div", "ssv-head");
  const title = el("div", "ssv-title");
  title.append(el("strong", undefined, s.name),
    el("span", "ssv-sub", `${s.n_sessions} visit${s.n_sessions === 1 ? "" : "s"} · ${s.n_frames} frames · located from ${s.accuracy.n_fixes} GPS fixes` +
      (s.accuracy.median_m !== null ? ` (median ±${Math.round(s.accuracy.median_m)} m)` : "")));
  const tabs = el("div", "ssv-tabs");
  tabs.setAttribute("role", "tablist");
  for (const [k, label] of [["frames", "Frames"], ["model", "3D model"]] as const) {
    const t = el("button", `ssv-tab${V.tab === k ? " on" : ""}`, label);
    t.type = "button";
    t.setAttribute("role", "tab");
    t.setAttribute("aria-selected", String(V.tab === k));
    t.addEventListener("click", () => {
      if (!V || V.tab === k) return;
      V.tab = k;
      render();
    });
    tabs.append(t);
  }
  head.append(title, tabs, closeButton());
  const body = el("div", "ssv-body");
  root.replaceChildren(head, body);
  root.classList.toggle("ssv-fs", V.tab === "frames" && V.fs);
  if (V.tab === "frames") renderFrames(body);
  else void renderModel(body);
}

function renderFrames(body: HTMLElement): void {
  if (!V) return;
  const vis = visible();
  const main = el("div", "ssv-main");
  const stage = el("div", "ssv-stage");
  stage.tabIndex = 0;
  const side = el("div", "ssv-side");
  body.append(main, side);
  main.append(stage, el("div", "ssv-info"), el("div", "ssv-strip"));

  if (!vis.length) {
    stage.append(el("div", "ssv-empty", "No frames in this visit yet (they may still be uploading from the phone)."));
  } else {
    // two layers: the frame shown (.ssv-img) and the one it moves away from (.ssv-img-out), blended (row #32)
    // each layer is a frame AND its labels, so they arrive, move and fade together: no picture-then-labels
    // flicker (row #36)
    const out = el("img", "ssv-img-out");
    out.alt = "";
    out.setAttribute("aria-hidden", "true");
    const outLayer = el("div", "ssv-layer ssv-layer-out");
    outLayer.setAttribute("aria-hidden", "true");
    outLayer.append(out, el("div", "ssv-labels-out"));
    const img = el("img", "ssv-img");
    img.alt = "Scan frame (faces and number plates blurred)";
    img.decoding = "async";
    img.addEventListener("load", () => { if (!root?.querySelector(".ssv-stage.moving")) renderLabels(); });
    const labels = el("div", "ssv-labels");
    const inLayer = el("div", "ssv-layer ssv-layer-in");
    inLayer.append(img, labels);
    const prev = el("button", "ssv-nav ssv-prev", "‹");
    const next = el("button", "ssv-nav ssv-next", "›");
    prev.type = next.type = "button";
    prev.title = "Back along the path (←)";
    next.title = "Forward along the path (→)";
    prev.addEventListener("click", () => go(-1));
    next.addEventListener("click", () => go(1));
    const badges = el("div", "ssv-badges");
    const play = el("button", "ssv-play");
    play.type = "button";
    play.addEventListener("click", () => (V?.playing ? stopPlay() : startPlay()));
    const fsBtn = el("button", "ssv-fs-toggle", "⛶");
    fsBtn.type = "button";
    fsBtn.title = "Full screen";
    fsBtn.setAttribute("aria-label", "Full screen");
    fsBtn.addEventListener("click", () => enterFullScreen());
    // in full screen: minimise (back to the viewer) and close (the whole viewer), always visible
    const bar = el("div", "ssv-fs-bar");
    const minBtn = el("button", "ssv-fs-min", "⤡");
    minBtn.type = "button";
    minBtn.title = "Leave full screen (back)";
    minBtn.setAttribute("aria-label", "Leave full screen");
    minBtn.addEventListener("click", () => leaveFullScreen());
    const xBtn = el("button", "ssv-fs-close", "×");
    xBtn.type = "button";
    xBtn.title = "Close the viewer";
    xBtn.setAttribute("aria-label", "Close the viewer");
    xBtn.addEventListener("click", () => closeSiteViewer());
    bar.append(el("span", "ssv-fs-count"), minBtn, xBtn);
    const overlay = el("button", `ssv-overlay${V.showAssets ? " on" : ""}`, "🏷");
    overlay.type = "button";
    overlay.title = V.showAssets ? "Hide the labels" : "Show the labels";
    overlay.setAttribute("aria-label", "Labels on the picture");
    overlay.setAttribute("aria-pressed", String(V.showAssets));
    overlay.addEventListener("click", () => setOverlay(!V?.showAssets));
    const panoBtn = el("button", "ssv-pano-btn");
    panoBtn.type = "button";
    panoBtn.hidden = true;
    panoBtn.addEventListener("click", () => openPano());
    stage.append(outLayer, inLayer, prev, next, play, badges, fsBtn, overlay, panoBtn, bar, el("div", "ssv-toast"));
    // swipe (touch / pen / mouse drag)
    let down: { x: number; y: number } | null = null;
    stage.addEventListener("pointerdown", (e) => {
      if ((e.target as HTMLElement).closest("button, .ssv-namer, .ssv-label, .ssv-name")) return;
      down = { x: e.clientX, y: e.clientY };
    });
    stage.addEventListener("pointerup", (e) => {
      if (!down) return;
      const dx = e.clientX - down.x, dy = e.clientY - down.y;
      down = null;
      // a tap on the picture marks an item there (row #36: no mode to switch on first)
      if (Math.hypot(dx, dy) < 10 && !(e.target as HTMLElement).closest(".ssv-namer")) {
        openNamer(e.clientX, e.clientY);
        return;
      }
      const step = swipeStep(dx, dy);
      if (step) go(step);
    });
    new ResizeObserver(() => renderLabels()).observe(stage);
  }
  // side: timeline, asset toggle, mini-map
  side.append(timeline(), assetToggle(), el("div", "ssv-naming"), el("div", "ssv-mini"));
  renderNamingBox();
  renderStrip();
  showFrame();
  initMini();
}

function timeline(): HTMLElement {
  const box = el("div", "ssv-timeline");
  box.append(el("h4", undefined, "Visits"));
  const list = el("ol", "ssv-visits");
  const all = el("li");
  const allBtn = el("button", `ssv-visit${V?.visit === null ? " on" : ""}`, `All visits (${V?.frames.length ?? 0} frames)`);
  allBtn.type = "button";
  allBtn.addEventListener("click", () => pickVisit(null));
  all.append(allBtn);
  list.append(all);
  for (const s of V?.site.sessions ?? []) {
    const li = el("li");
    const b = el("button", `ssv-visit role-${s.role ?? "current"}${V?.visit === s.client_uuid ? " on" : ""}`);
    b.type = "button";
    b.title = s.capture_type === "as_built"
      ? "During works / as-built: a separate, permanent record. Never merged into the current view and never superseded."
      : s.role === "history" ? `Older than the current window (${V?.site.current_window_months ?? 24} months): kept as history, not part of the current view.`
        : "Part of the site's current view.";
    b.append(el("span", "ssv-visit-label", visitLabel(s)), el("small", undefined,
      `${s.n_frames} frames${s.capture_type === "as_built" ? "" : ` · ${s.role === "history" ? "history" : "current"}`}${s.kind === "project" && s.project_name && s.capture_type !== "as_built" ? ` · ${s.project_name}` : ""}`));
    b.addEventListener("click", () => pickVisit(s.client_uuid));
    li.append(b);
    list.append(li);
  }
  box.append(list);
  return box;
}

function assetToggle(): HTMLElement {
  const lab = el("label", "ssv-toggle");
  const cb = el("input");
  cb.type = "checkbox";
  cb.checked = !!V?.showAssets;
  cb.addEventListener("change", () => {
    if (!V) return;
    setOverlay(cb.checked);
  });
  lab.append(cb, el("span", undefined, "Label WRAP assets (approximate)"));
  return lab;
}

function pickVisit(session: string | null): void {
  if (!V) return;
  const cur = visible()[V.i];
  V.visit = session;
  const vis = visible();
  const k = cur ? vis.findIndex((f) => f.session === cur.session && f.idx === cur.idx) : -1;
  V.i = k >= 0 ? k : 0;
  render();
}

function go(delta: number, fromPlay = false): void {
  if (!V) return;
  if (!fromPlay) stopPlay();
  const n = visible().length;
  const j = stepIndex(V.i, delta, n);
  if (j === V.i) return;
  const from = V.i;
  V.i = j;
  showFrame(from);
}

function jump(i: number): void {
  if (!V) return;
  stopPlay();
  const from = V.i;
  V.i = stepIndex(i, 0, visible().length);
  showFrame(from);
}

// --- panoramas (row #38, 2026-10-19) -----------------------------------------
//
// Where someone stood and turned, the scan worker stitches those frames into a strip (scan_panoramas.py). A
// frame that's part of one shows a "Pan" button; it opens the strip at that frame's heading, to drag left and
// right (or the arrow keys). Back, Escape or × return to the photos. Works in full screen.

let panoOpen: { pano: Pano; x: number; img: HTMLImageElement; box: HTMLElement } | null = null;
let releasePanoBack: (() => void) | null = null;

async function loadPanoramas(): Promise<void> {
  if (!V) return;
  const site = V.site.id;
  try {
    const r = await getJson<{ panoramas: Pano[] }>(`${API}/sites/${site}/panoramas`);
    if (!V || V.site.id !== site) return;
    V.panos = r.panoramas ?? [];
    V.panoOf = new Map();
    for (const p of V.panos) {
      for (const m of p.members ?? p.idxs.map((idx) => ({ session: p.session, idx, heading: 0 }))) V.panoOf.set(`${m.session}:${m.idx}`, p);
    }
    syncPanoButton();
  } catch {
    // none: the frames as they are
  }
}

function syncPanoButton(): void {
  const b = root?.querySelector<HTMLButtonElement>(".ssv-pano-btn");
  const f = V ? visible()[V.i] : undefined;
  if (!b || !V) return;
  const p = f ? V.panoOf.get(`${f.session}:${f.idx}`) : undefined;
  b.hidden = !p;
  if (p) {
    const deg = Math.round(panoDegrees(p));
    b.textContent = `⟷ ${deg}°`;
    b.title = `Look around: ${deg}° of 360° captured here, stitched from the frames`;
    b.setAttribute("aria-label", `Look around here: ${deg} of 360 degrees captured`);
  }
}

/** The strip's left edge (stage px) that puts heading `h` (unwrapped like the panorama's) in the middle. */
function panoOffsetFor(p: Pano, h: number, stripW: number, stageW: number): number {
  const lo = p.heading_left_deg ?? Math.min(...p.headings) - p.hfov_deg / 2;
  const frac = Math.max(0, Math.min(1, (h - lo) / panoDegrees(p)));
  return stageW / 2 - frac * stripW;
}

/** The "N of 360 degrees" marker: a ring with the captured arc, and where you're looking (row #44). */
function panoRing(p: Pano): HTMLElement {
  const box = el("div", "ssv-pano-ring");
  const deg = panoDegrees(p);
  const r = 18, c = 2 * Math.PI * r;
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 44 44");
  svg.setAttribute("width", "44");
  svg.setAttribute("height", "44");
  svg.setAttribute("aria-hidden", "true");
  const ring = (cls: string, frac: number, rot: number) => {
    const e = document.createElementNS(ns, "circle");
    e.setAttribute("cx", "22");
    e.setAttribute("cy", "22");
    e.setAttribute("r", String(r));
    e.setAttribute("class", cls);
    e.setAttribute("stroke-dasharray", `${(frac * c).toFixed(1)} ${c.toFixed(1)}`);
    e.setAttribute("transform", `rotate(${rot.toFixed(1)} 22 22)`);
    svg.append(e);
    return e;
  };
  const lo = p.heading_left_deg ?? Math.min(...p.headings) - p.hfov_deg / 2;
  ring("ssv-ring-all", 1, 0);
  ring("ssv-ring-got", deg / 360, lo - 90);           // north at the top, clockwise
  const view = ring("ssv-ring-view", 0.06, lo - 90);
  view.dataset.lo = String(lo);
  box.append(svg, el("span", "ssv-ring-text", `${Math.round(deg)}° of 360°`));
  return box;
}

function openPano(): void {
  if (!root || !V || panoOpen) return;
  const stage = root.querySelector<HTMLElement>(".ssv-stage");
  const f = visible()[V.i];
  const p = f ? V.panoOf.get(`${f.session}:${f.idx}`) : undefined;
  if (!stage || !f || !p) return;
  stopPlay();
  const box = el("div", "ssv-pano");
  box.setAttribute("role", "dialog");
  box.setAttribute("aria-label", "Panorama: drag to look around");
  const img = el("img", "ssv-pano-img");
  img.alt = "Panorama stitched from the frames taken here (faces and number plates blurred)";
  img.draggable = false;
  const close = el("button", "ssv-pano-close", "×");
  close.type = "button";
  close.title = "Back to the photos";
  close.setAttribute("aria-label", "Back to the photos");
  close.addEventListener("click", () => closePano());
  const lined = p.alignment_px ? ` · lined up to ~${Math.max(1, Math.round(p.alignment_px.median))} px` : "";
  box.append(img, close, panoRing(p), el("div", "ssv-pano-hint", `Drag to look around · ${Math.round(panoDegrees(p))}° of 360° captured${lined}`));
  stage.append(box);
  new ResizeObserver(() => panBy(0)).observe(box);     // full screen on / off, the phone turned
  const mem = p.members?.find((m) => m.session === f.session && m.idx === f.idx);
  const k = p.idxs.indexOf(f.idx);
  const heading = mem ? mem.heading : k >= 0 && f.session === p.session ? p.headings[k] : p.headings[Math.floor(p.headings.length / 2)];
  panoOpen = { pano: p, x: 0, img, box };
  const place = () => {
    if (!panoOpen || panoOpen.box !== box) return;
    const stripW = (box.clientHeight / p.height) * p.width;
    panoOpen.x = panoOffsetFor(p, heading, stripW, box.clientWidth);
    panBy(0);
  };
  img.addEventListener("load", place);
  img.src = p.url;
  // drag to pan (the stage's own swipe never sees it)
  let drag: { x: number; at: number; id: number } | null = null;
  box.addEventListener("pointerdown", (e) => {
    if ((e.target as HTMLElement).closest("button")) return;
    e.stopPropagation();
    drag = { x: e.clientX, at: panoOpen?.x ?? 0, id: e.pointerId };
    box.setPointerCapture(e.pointerId);
    box.classList.add("dragging");
  });
  box.addEventListener("pointermove", (e) => {
    if (!drag || e.pointerId !== drag.id || !panoOpen) return;
    panoOpen.x = drag.at + (e.clientX - drag.x);
    panBy(0);
  });
  const end = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return;
    drag = null;
    box.classList.remove("dragging");
    e.stopPropagation();
  };
  box.addEventListener("pointerup", end);
  box.addEventListener("pointercancel", end);
  releasePanoBack = closeOnBack(() => {
    releasePanoBack = null;
    closePano();
  });
}

/** Move the strip by `dx` px, kept so it always fills the stage (or is centred when narrower). */
function panBy(dx: number): void {
  if (!panoOpen) return;
  const { pano: p, img, box } = panoOpen;
  const W = box.clientWidth, H = box.clientHeight;
  const stripW = (H / p.height) * p.width;
  let x = panoOpen.x + dx;
  x = stripW <= W ? (W - stripW) / 2 : Math.min(0, Math.max(W - stripW, x));
  panoOpen.x = x;
  img.style.height = `${H}px`;
  img.style.width = `${stripW}px`;
  img.style.transform = `translateX(${x.toFixed(1)}px)`;
  // the ring's "you're looking here" arc
  const view = box.querySelector<SVGCircleElement>(".ssv-ring-view");
  if (view && stripW > 0) {
    const deg = panoDegrees(p);
    const lo = Number(view.dataset.lo ?? 0);
    const from = lo + (Math.max(0, -x) / stripW) * deg, width = Math.min(1, W / stripW) * deg;
    const c = 2 * Math.PI * 18;
    view.setAttribute("stroke-dasharray", `${((width / 360) * c).toFixed(1)} ${c.toFixed(1)}`);
    view.setAttribute("transform", `rotate(${(from - 90).toFixed(1)} 22 22)`);
  }
}

function closePano(): void {
  if (!panoOpen) return;
  panoOpen.box.remove();
  panoOpen = null;
  releasePanoBack?.();
  releasePanoBack = null;
}

// --- full screen (row #35, 2026-10-19) -----------------------------------------
//
// The frame alone, full size, edge to edge, with stepping, swipes and play as usual, and an always-visible bar:
// minimise (back to the viewer) and close. The phone's back gesture, Escape and F leave it first. Where the
// browser allows, the page itself goes full screen too (hiding the phone's bars); leaving that by the system's
// own gesture leaves ours.

function enterFullScreen(): void {
  if (!root || !V || V.fs || V.tab !== "frames") return;
  V.fs = true;
  root.classList.add("ssv-fs");
  releaseFsBack = closeOnBack(() => {
    releaseFsBack = null;
    leaveFullScreen();
  });
  try {
    if (!document.fullscreenElement && root.requestFullscreen) void root.requestFullscreen({ navigationUI: "hide" }).catch(() => undefined);
  } catch {
    // CSS full screen only
  }
  document.addEventListener("fullscreenchange", onFullscreenChange);
  showFrame();
}

/** Leave full screen; `rerender` false when the viewer is closing anyway. */
function leaveFullScreen(rerender = true): void {
  if (!V?.fs) return;
  V.fs = false;
  document.removeEventListener("fullscreenchange", onFullscreenChange);
  releaseFsBack?.();
  releaseFsBack = null;
  try {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
  } catch {
    // not in the browser's full screen
  }
  root?.classList.remove("ssv-fs");
  if (rerender) showFrame();
}

function onFullscreenChange(): void {
  if (!document.fullscreenElement && V?.fs) leaveFullScreen();
}

// --- smooth moves (row #32, 2026-10-19) --------------------------------------
//
// A step blends the next frame in while the last one moves the way the camera did: turned (it slides), walked on
// (it zooms in), stepped sideways (parallax at the view's depth). With a site model the move comes from its SfM
// cameras (model/poses); otherwise from the phone's compass and tilt plus a nominal step along the path. A jump
// (thumbnail, map, Home/End) is a plain crossfade. Reduced motion: a short crossfade only.

interface PosesDoc {
  session: string | null;
  intrinsics: { width: number; height: number; fx: number; fy: number } | null;
  ground_z: number | null;
  poses: { idx: number; centre: [number, number, number]; right: [number, number, number]; up: [number, number, number]; fwd: [number, number, number] }[];
}

async function loadPoses(): Promise<void> {
  if (!V) return;
  const site = V.site.id;
  try {
    const r = await getJson<PosesDoc>(`${API}/sites/${site}/model/poses`);
    if (!V || V.site.id !== site || !r.session || !r.intrinsics) return;
    // the SfM's scale is unverified (often several times out): rescale so the median step between consecutive
    // frames is WRAP's capture step (~0.6 m), which is what the depth in frameMove is in
    const k = poseScale(r.poses);
    const sc = (c: [number, number, number]): [number, number, number] => [c[0] * k, c[1] * k, c[2] * k];
    for (const p of r.poses) V.poses.set(`${r.session}:${p.idx}`, { centre: sc(p.centre), right: p.right, up: p.up, fwd: p.fwd,
      ground_z: r.ground_z === null ? null : r.ground_z * k });
    V.poseFocal = { fx: r.intrinsics.fx / r.intrinsics.width, fy: r.intrinsics.fy / r.intrinsics.height };
    V.poseUp = meanUp(r.poses.map((p) => p.up));
    renderLabels();
  } catch {
    // no model, or no poses in it: compass moves
  }
}

/** Metres per model unit, taking the median step between consecutive frames as WRAP's ~0.6 m capture step
 * (1 when there are too few steps). */
function poseScale(poses: { idx: number; centre: [number, number, number] }[]): number {
  const steps: number[] = [];
  for (let i = 1; i < poses.length; i++) {
    if (poses[i].idx !== poses[i - 1].idx + 1) continue;
    const a = poses[i - 1].centre, b = poses[i].centre;
    const d = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
    if (d > 0) steps.push(d);
  }
  if (steps.length < 3) return 1;
  steps.sort((x, y) => x - y);
  return 0.6 / steps[Math.floor(steps.length / 2)];
}

/** A frame's camera from its compass + tilt (no centre: GPS between neighbouring frames is noise). */
function compassPose(f: SiteFrame): FramePose | null {
  if (f.heading_deg === null) return null;
  const [beta, gamma] = frameBetaGamma(f);
  const b = cameraBasis(f.heading_deg, beta, gamma, f.screen_angle || 0);
  return b ? { centre: null, right: b.right, up: b.up, fwd: b.fwd } : null;
}

/** The move from frame `a` to `b`, or null for a plain crossfade: from the model's cameras for any pair
 * placed in it, else from the compass between neighbours along the path (`adjacent`). */
function moveFor(a: SiteFrame, b: SiteFrame, adjacent = true): FrameMove | null {
  if (!V) return null;
  const pa = V.poses.get(`${a.session}:${a.idx}`), pb = V.poses.get(`${b.session}:${b.idx}`);
  if (pa && pb && V.poseFocal) return frameMove(pa, pb, V.poseFocal.fx, V.poseFocal.fy);
  if (!adjacent) return null;
  const ca = compassPose(a), cb = compassPose(b);
  if (!ca || !cb || a.session !== b.session) return null;
  const w = a.width || 720, h = a.height || 1280;
  const focal = focalPx(Math.max(w, h), a.fov_deg ?? 63);
  // WRAP keeps a frame every ~0.6 m of walking: a nominal step forward / back along the path
  return frameMove(ca, cb, focal / w, focal / h, b.pos > a.pos ? 0.6 : -0.6);
}

function reducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Show `src` in the main layer, blending from what it showed with `move` (null = crossfade). */
async function blendTo(img: HTMLImageElement, out: HTMLImageElement, stage: HTMLElement, src: string, move: FrameMove | null): Promise<void> {
  const seq = ++moveSeq;
  const had = img.currentSrc || img.src;
  if (!had || had === new URL(src, window.location.href).href) {
    img.src = src;
    return;
  }
  // the outgoing layer takes the picture just shown (already decoded: no flash) and its labels, as they are
  const outL = out.closest<HTMLElement>(".ssv-layer") ?? out;
  const inL = img.closest<HTMLElement>(".ssv-layer") ?? img;
  const outLabels = outL.querySelector<HTMLElement>(".ssv-labels-out");
  const inLabels = inL.querySelector<HTMLElement>(".ssv-labels");
  outL.style.transition = "none";
  outL.style.transform = "none";
  outL.style.opacity = "1";
  out.src = had;
  if (outLabels && inLabels) outLabels.replaceChildren(...Array.from(inLabels.childNodes));
  inL.style.transition = "none";
  inL.style.opacity = "0";
  inL.style.transform = "none";
  stage.classList.add("moving");
  img.src = src;
  try {
    await img.decode();
  } catch {
    // shown when it loads
  }
  if (seq !== moveSeq) return;                 // a newer step took over
  renderLabels();                               // the new frame's labels, drawn before it shows: they arrive with it
  const W = stage.clientWidth, H = stage.clientHeight;
  const reduce = reducedMotion();
  const ms = reduce ? 160 : MOVE_MS;
  const m = reduce ? null : move;
  const outEnd = m ? `translate(${(m.dx * W).toFixed(1)}px, ${(m.dy * H).toFixed(1)}px) scale(${m.zoom.toFixed(3)})` : "none";
  const inStart = m ? `translate(${(-m.dx * W * 0.6).toFixed(1)}px, ${(-m.dy * H * 0.6).toFixed(1)}px) scale(${(1 / m.zoom).toFixed(3)})` : "none";
  inL.style.transform = inStart;
  void inL.offsetWidth;                         // commit the start state before transitioning
  const ease = "cubic-bezier(0.25, 0.1, 0.25, 1)";
  inL.style.transition = `opacity ${ms}ms ${ease}, transform ${ms}ms ${ease}`;
  outL.style.transition = `opacity ${ms}ms ${ease}, transform ${ms}ms ${ease}`;
  inL.style.opacity = "1";
  inL.style.transform = "none";
  outL.style.opacity = "0";
  outL.style.transform = outEnd;
  window.setTimeout(() => {
    if (seq !== moveSeq) return;
    stage.classList.remove("moving");
    out.removeAttribute("src");
    outLabels?.replaceChildren();
    outL.style.transition = inL.style.transition = "none";
  }, ms + 30);
}

function startPlay(): void {
  if (!V || V.playing) return;
  if (V.i >= visible().length - 1) jump(0);
  V.playing = true;
  syncPlay();
  const tick = () => {
    if (!V || !V.playing) return;
    if (V.i >= visible().length - 1) {
      stopPlay();
      return;
    }
    go(1, true);
    playTimer = window.setTimeout(tick, PLAY_MS);
  };
  playTimer = window.setTimeout(tick, 150);
}

function stopPlay(): void {
  if (playTimer !== null) window.clearTimeout(playTimer);
  playTimer = null;
  if (V?.playing) {
    V.playing = false;
    syncPlay();
  }
}

function syncPlay(): void {
  const b = root?.querySelector<HTMLButtonElement>(".ssv-play");
  if (!b) return;
  const on = !!V?.playing;
  b.textContent = on ? "❚❚" : "▶";
  b.title = on ? "Pause (space)" : "Walk along the path (space)";
  b.setAttribute("aria-label", on ? "Pause" : "Play: walk along the path");
  b.classList.toggle("on", on);
}

function showFrame(from?: number): void {
  if (!root || !V) return;
  const vis = visible();
  const f = vis[V.i];
  const img = root.querySelector<HTMLImageElement>(".ssv-img");
  const out = root.querySelector<HTMLImageElement>(".ssv-img-out");
  const stage = root.querySelector<HTMLElement>(".ssv-stage");
  if (f && img) {
    const size = V.fs ? "full" : "medium";
    const src = frameUrl(f, size);
    const a = from !== undefined ? vis[from] : undefined;
    if (out && stage && a) void blendTo(img, out, stage, src, moveFor(a, f, Math.abs((from ?? 0) - V.i) === 1));
    else img.src = src;
    img.dataset.w = String(f.width ?? 0);
    img.dataset.h = String(f.height ?? 0);
    for (const k of prefetchOrder(V.i, vis.length)) {
      const p = new Image();
      p.decoding = "async";
      p.src = frameUrl(vis[k], size);
    }
  }
  const prev = root.querySelector<HTMLButtonElement>(".ssv-prev");
  const next = root.querySelector<HTMLButtonElement>(".ssv-next");
  if (prev) prev.disabled = V.i <= 0;
  if (next) next.disabled = V.i >= vis.length - 1;
  renderInfo(f);
  renderBadges(f);
  syncPlay();
  const count = root.querySelector(".ssv-fs-count");
  if (count) count.textContent = f ? `${V.i + 1} / ${vis.length}` : "";
  syncPanoButton();
  if (!stage?.classList.contains("moving")) renderLabels();
  // strip: highlight and keep in view
  root.querySelectorAll(".ssv-thumb.on").forEach((t) => t.classList.remove("on"));
  const th = root.querySelector<HTMLElement>(`.ssv-thumb[data-k="${V.i}"]`);
  if (th) {
    th.classList.add("on");
    th.scrollIntoView({ block: "nearest", inline: "center" });
  }
  updateMini();
}

function renderInfo(f: SiteFrame | undefined): void {
  const box = root?.querySelector(".ssv-info");
  if (!box || !V) return;
  if (!f) {
    box.replaceChildren();
    return;
  }
  const v = visitOf(f.session);
  const when = f.t_ms ? new Date(f.t_ms) : new Date(f.received_at);
  const parts = [
    `${V.i + 1} / ${visible().length}`,
    when.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }),
    v ? `recorded by ${v.recorder}` : "",
    f.heading_deg !== null ? `facing ${Math.round(f.heading_deg)}°` : "no compass",
    f.pos_sigma_m !== null ? `position ±${Math.round(f.pos_sigma_m)} m (${f.pos_source === "estimate" ? "tracked" : "GPS"})` : "no position",
    f.blurred.faces + f.blurred.plates ? `${f.blurred.faces} face(s), ${f.blurred.plates} plate(s) blurred` : "",
  ].filter(Boolean);
  // full size in the viewer's own full screen, not a bare image in a new window: the installed app (PWA) had
  // no way back from that (row #35)
  const full = el("button", "ssv-full", "Full screen");
  full.type = "button";
  full.addEventListener("click", () => enterFullScreen());
  box.replaceChildren(el("span", undefined, parts.join(" · ")), full);
}

function renderBadges(f: SiteFrame | undefined): void {
  const box = root?.querySelector(".ssv-badges");
  if (!box) return;
  box.replaceChildren();
  if (!f) return;
  const v = visitOf(f.session);
  if (v?.capture_type === "as_built") box.append(el("span", "ssv-badge asbuilt", visitLabel(v)));
  else if (v?.role === "history") box.append(el("span", "ssv-badge history", `History · ${shortDate(v.started_at)}`));
}

function renderStrip(): void {
  const strip = root?.querySelector(".ssv-strip");
  if (!strip || !V) return;
  const vis = visible();
  const frag = document.createDocumentFragment();
  vis.forEach((f, k) => {
    const b = el("button", "ssv-thumb");
    b.type = "button";
    b.dataset.k = String(k);
    b.title = `Frame ${k + 1}`;
    const im = el("img");
    im.loading = "lazy";
    im.decoding = "async";
    im.alt = "";
    im.src = frameUrl(f, "thumb");
    b.append(im);
    b.addEventListener("click", () => jump(k));
    frag.append(b);
  });
  strip.replaceChildren(frag);
}

/** The frame's overlay: the approximate WRAP asset labels, then the assets people named on it. */
function renderLabels(): void {
  renderAssetLabels();
  renderNames();
  renderHotspots();
}

// --- Street View-style hotspots (row #37) ------------------------------------
//
// Discs on the ground where other frames were taken, one per way you can go: tap one to move there (with the
// pose-aware move). From the site model's cameras when this frame is placed in it; otherwise from the frames'
// GPS and compass, only for points 3-25 m away (GPS is metres out, so nearer ones would point anywhere).

function renderHotspots(): void {
  const box = root?.querySelector<HTMLElement>(".ssv-labels");
  const f = V ? visible()[V.i] : undefined;
  const g = imgGeom();
  if (!box || !V || !f || !g) return;
  box.querySelectorAll(".ssv-hotspot").forEach((e) => e.remove());
  const vis = visible();
  const here = V.poses.get(`${f.session}:${f.idx}`);
  let spots: ReturnType<typeof hotspots> = [];
  if (here && V.poseFocal) {
    const cands = vis.map((q, k) => ({ k, pose: V!.poses.get(`${q.session}:${q.idx}`) }))
      .filter((c): c is { k: number; pose: FramePose } => !!c.pose && c.k !== V!.i);
    spots = hotspots(here, cands, V.poseFocal.fx, V.poseFocal.fy, V.poseUp);
  } else if (f.pos_lat !== null && f.pos_lon !== null) {
    const base = compassPose(f);
    if (base) {
      const lat0 = f.pos_lat, lon0 = f.pos_lon;
      const w = f.width || g.w, h = f.height || g.h;
      const focal = focalPx(Math.max(w, h), f.fov_deg ?? 63);
      const cands = vis.map((q, k) => ({ q, k }))
        .filter(({ q, k }) => k !== V!.i && q.pos_lat !== null && q.pos_lon !== null && (q.pos_sigma_m ?? 99) <= 8)
        .map(({ q, k }) => {
          const [e, n] = enuOffset(lat0, lon0, q.pos_lat!, q.pos_lon!);
          return { k, pose: { centre: [e, n, 0] as [number, number, number], right: base.right, up: base.up, fwd: base.fwd } };
        });
      spots = hotspots({ ...base, centre: [0, 0, 0] }, cands, focal / w, focal / h, [0, 0, 1], { minM: 3, maxM: 25, heightM: 1.5, binDeg: 25, limit: 4 });
    }
  }
  const dispW = g.w * g.scale, dispH = g.h * g.scale;
  const drawn: [number, number, number][] = [];
  for (const s of spots) {
    const size = Math.max(28, Math.min(72, 84 - s.dist_m * 4));
    const px = g.ox + s.x * dispW, py = g.oy + s.y * dispH;
    // one that would sit on a nearer one (they're nearest first) is left out: it couldn't be tapped
    if (drawn.some(([x, y, r]) => Math.hypot(px - x, py - y) < (r + size) / 2 + 6)) continue;
    drawn.push([px, py, size]);
    const b = el("button", "ssv-hotspot");
    b.type = "button";
    b.style.left = `${px}px`;
    b.style.top = `${py}px`;
    b.style.width = `${size}px`;
    b.style.height = `${size * 0.42}px`;
    b.title = `Move here (~${Math.round(s.dist_m)} m)`;
    b.setAttribute("aria-label", `Move about ${Math.round(s.dist_m)} metres ${s.bearing_deg > 15 ? "to the right" : s.bearing_deg < -15 ? "to the left" : "ahead"}`);
    b.append(el("span", "ssv-hotspot-chev", "⌃"));
    b.addEventListener("click", (e) => {
      e.stopPropagation();
      stopPlay();
      if (!V) return;
      const from = V.i;
      V.i = s.k;
      showFrame(from);
    });
    box.append(b);
  }
}

/** WRAP asset labels from the frame's stored pose (arMath projection). */
function renderAssetLabels(): void {
  const box = root?.querySelector<HTMLElement>(".ssv-labels");
  const img = root?.querySelector<HTMLImageElement>(".ssv-img");
  if (!box || !img || !V) return;
  box.replaceChildren();
  const f = visible()[V.i];
  if (!f || !V.showAssets || !V.assets.length || f.heading_deg === null) {
    if (f && V.showAssets && f.heading_deg === null) box.append(el("div", "ssv-approx", "No compass heading for this frame: assets can't be placed"));
    return;
  }
  const w = f.width || img.naturalWidth;
  const hgt = f.height || img.naturalHeight;
  if (!w || !hgt) return;
  const [beta, gamma] = frameBetaGamma(f);
  const basis = cameraBasis(f.heading_deg, beta, gamma, f.screen_angle || 0);
  if (!basis) return;
  const view = { basis, focal: focalPx(Math.max(w, hgt), f.fov_deg ?? 63), width: w, height: hgt };
  // an asset someone has already put in place on this frame (a drag, or a mark with its id) shows there instead
  const putHere = new Set(V.labels.filter((l) => l.session === f.session && l.idx === f.idx && l.asset_id).map((l) => l.asset_id));
  const labels: AssetLabel[] = assetLabels(f, V.assets, w, hgt, (e, n) => projectPoint(e, n, 0, view)).filter((l) => !putHere.has(l.asset_id));
  // the image is letterboxed inside the stage (object-fit: contain); frame pixels -> stage pixels
  const g = imgGeom();
  const sr = { width: root?.querySelector<HTMLElement>(".ssv-stage")?.clientWidth ?? 0 };
  if (!g) return;
  const scale = (g.w * g.scale) / w;
  const ox = g.ox, oy = g.oy;
  // nearest first; a label that would overlap one already placed is nudged
  // down a row (up to 3 times), else left out
  const narrow = sr.width < 560;
  const placed: [number, number, number, number][] = [];
  for (const l of labels.slice(0, narrow ? 6 : 12)) {
    const text = narrow ? `${l.asset_id} · ~${Math.round(l.distance_m)} m`
      : `${l.asset_id}${l.asset_type ? ` · ${l.asset_type}` : ""} · ~${Math.round(l.distance_m)} m`;
    const x = ox + l.x * scale;
    let y = oy + l.y * scale;
    const wpx = 22 + text.length * 7;
    let ok = false;
    for (let k = 0; k < 4 && !ok; k++) {
      ok = !placed.some(([a0, b0, a1, b1]) => x < a1 && x + wpx > a0 && y < b1 && y + 20 > b0);
      if (!ok) y += 22;
    }
    if (!ok) continue;
    placed.push([x, y, x + wpx, y + 20]);
    const t = el("div", "ssv-label");
    t.style.left = `${x}px`;
    t.style.top = `${y}px`;
    t.setAttribute("data-asset-id", l.asset_id);
    t.title = "Approximate. Drag it to where the asset really is in the picture to correct it.";
    t.append(el("span", "ssv-label-dot"), el("span", "ssv-label-text", text));
    makeDraggable(t, f, l.asset_id, V.assets.find((a) => a.asset_id === l.asset_id)?.asset_type ?? null);
    box.append(t);
  }
  box.append(el("div", "ssv-approx", labels.length
    ? "Approximate: placed from the phone's GPS and compass when the frame was taken (often metres out)."
    : "No WRAP assets in view (labels are approximate)."));
}

// --- naming assets on frames (2026-10-16) -------------------------------------------
//
// "This is BFP 41202": tap the asset on a frame and pick it from the nearby assets, or type / say its name. The
// scan worker puts each named asset in the 3D model from the frames it was named on (two or more, from different
// spots, pin it down) and uses it to place the model on the register. Labels are also kept as training data for a
// future detector. They never change the register.

/** Where the image is drawn inside the stage (object-fit: contain): offset and scale, in stage pixels. */
function imgGeom(): { ox: number; oy: number; scale: number; w: number; h: number } | null {
  // from the stage's size, not the image's on-screen box: the layers are moved / scaled while blending
  const stage = root?.querySelector<HTMLElement>(".ssv-stage");
  const img = root?.querySelector<HTMLImageElement>(".ssv-img");
  if (!stage || !img || !img.naturalWidth) return null;
  const w = img.naturalWidth, h = img.naturalHeight;
  const W = stage.clientWidth, H = stage.clientHeight;
  const scale = Math.min(W / w, H / h);
  return { ox: (W - w * scale) / 2, oy: (H - h * scale) / 2, scale, w, h };
}

function renderNames(): void {
  const box = root?.querySelector<HTMLElement>(".ssv-labels");
  const f = V ? visible()[V.i] : undefined;
  const g = imgGeom();
  if (!box || !V || !f || !g || !V.showAssets) return;
  for (const l of V.labels.filter((x) => x.session === f.session && x.idx === f.idx)) {
    const pin = el("div", `ssv-name${l.mine ? " mine" : ""}`);
    pin.style.left = `${g.ox + l.x * g.w * g.scale}px`;
    pin.style.top = `${g.oy + l.y * g.h * g.scale}px`;
    if (l.asset_id) pin.setAttribute("data-asset-id", l.asset_id);
    const typeName = l.asset_type ? itemTypes[l.asset_type] ?? l.asset_type : "";
    pin.append(el("span", "ssv-name-dot"), el("span", "ssv-name-text", l.asset_id && l.text ? `${l.text} (${l.asset_id})`
      : (l.asset_id ?? l.text ?? typeName)));
    if (l.mine) {
      const x = el("button", "ssv-name-del", "×");
      x.type = "button";
      x.title = "Remove this mark";
      x.setAttribute("aria-label", "Remove this mark");
      x.addEventListener("click", () => void removeName(l.id));
      pin.append(x);
    }
    box.append(pin);
  }
}

function renderNamingBox(): void {
  const box = root?.querySelector<HTMLElement>(".ssv-naming");
  if (!box || !V) return;
  const n = V.labels.length;
  const frames = new Set(V.labels.map((l) => `${l.session}:${l.idx}`)).size;
  const parts: HTMLElement[] = [el("h4", undefined, n ? `Marked items (${n})` : "Marked items"),
    el("p", "ssv-hint", "Tap the picture to mark an item: say what type it is (a cage, a cover, a meter box). " +
      "Drag a WRAP label to where the asset really is to correct it.")];
  if (n) {
    parts.push(el("p", "ssv-hint", `${n} mark${n === 1 ? "" : "s"} on ${frames} frame${frames === 1 ? "" : "s"}. Tap one to go to it.`));
    // each mark, by visit and frame: a jump there (row #43: they were saved, but only findable frame by frame)
    const list = el("ul", "ssv-marks");
    const sorted = [...V.labels].sort((a, b) => a.session.localeCompare(b.session) || a.idx - b.idx);
    for (const l of sorted) {
      const li = el("li");
      const b = el("button", "ssv-mark-go");
      b.type = "button";
      const v = visitOf(l.session);
      const what = l.asset_id ?? l.text ?? (l.asset_type ? itemTypes[l.asset_type] ?? l.asset_type : "item");
      b.append(el("span", "ssv-name-dot"), el("span", undefined, what),
        el("small", undefined, ` · ${v ? shortDate(v.started_at) : "visit"} · frame ${l.idx + 1}`));
      b.addEventListener("click", () => goToFrame(l.session, l.idx));
      li.append(b);
      list.append(li);
    }
    parts.push(list);
  }
  box.replaceChildren(...parts);
}

/** Show a visit's frame (switching to that visit if needed). */
function goToFrame(session: string, idx: number): void {
  if (!V) return;
  stopPlay();
  if (V.visit !== null && V.visit !== session) {
    V.visit = session;
    V.tab = "frames";
    const k = visible().findIndex((f) => f.idx === idx);
    V.i = k >= 0 ? k : 0;
    render();
    return;
  }
  const k = visible().findIndex((f) => f.session === session && f.idx === idx);
  if (k < 0) return;
  if (V.tab !== "frames") {
    V.tab = "frames";
    V.i = k;
    render();
    return;
  }
  jump(k);
}

/** A WRAP asset label the person can drag to where the asset really is in the frame: saved as an audited
 * correction (a label with the asset's id, source "drag"). It never changes the register. */
function makeDraggable(t: HTMLElement, f: SiteFrame, assetId: string, assetType: string | null): void {
  t.classList.add("draggable");
  let start: { x: number; y: number; l: number; t: number; id: number } | null = null;
  t.addEventListener("pointerdown", (e) => {
    if (!V) return;
    e.stopPropagation();
    start = { x: e.clientX, y: e.clientY, l: parseFloat(t.style.left), t: parseFloat(t.style.top), id: e.pointerId };
    t.setPointerCapture(e.pointerId);
    t.classList.add("dragging");
  });
  t.addEventListener("pointermove", (e) => {
    if (!start || e.pointerId !== start.id) return;
    t.style.left = `${start.l + e.clientX - start.x}px`;
    t.style.top = `${start.t + e.clientY - start.y}px`;
  });
  const end = (e: PointerEvent, cancelled: boolean) => {
    if (!start || e.pointerId !== start.id) return;
    const moved = Math.hypot(e.clientX - start.x, e.clientY - start.y);
    const s0 = start;
    start = null;
    t.classList.remove("dragging");
    e.stopPropagation();
    if (cancelled || moved < 8) {
      t.style.left = `${s0.l}px`;
      t.style.top = `${s0.t}px`;
      return;
    }
    const g = imgGeom();
    if (!g || !V) return;
    const x = (parseFloat(t.style.left) - g.ox) / (g.w * g.scale);
    const y = (parseFloat(t.style.top) - g.oy) / (g.h * g.scale);
    if (x < 0 || x > 1 || y < 0 || y > 1) {
      t.style.left = `${s0.l}px`;
      t.style.top = `${s0.t}px`;
      toast("Dropped off the picture: not moved.");
      return;
    }
    void saveMark(f, { x, y, asset_id: assetId, asset_type: assetType, text: null, source: "drag" })
      .then((ok) => toast(ok ? `${assetId} put here (a correction; it doesn't change the register).` : "Not saved."));
  };
  t.addEventListener("pointerup", (e) => end(e, false));
  t.addEventListener("pointercancel", (e) => end(e, true));
}

async function saveMark(f: SiteFrame, m: { x: number; y: number; asset_id: string | null; asset_type: string | null; text: string | null; source: string }): Promise<boolean> {
  if (!V) return false;
  try {
    const res = await fetch(`${API}/sites/${V.site.id}/labels`, {
      method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session: f.session, idx: f.idx, ...m }),
    });
    const d = await res.json().catch(() => null);
    if (!res.ok) throw new Error(d?.detail ?? `HTTP ${res.status}`);
    if (!V) return false;
    V.labels.push(d as SiteLabel);
    renderLabels();
    renderNamingBox();
    return true;
  } catch {
    return false;
  }
}

let toastTimer: number | null = null;
function toast(text: string): void {
  const t = root?.querySelector<HTMLElement>(".ssv-toast");
  if (!t) return;
  t.textContent = text;
  t.classList.add("on");
  if (toastTimer !== null) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => t.classList.remove("on"), 3200);
}

function closeNamer(): void {
  root?.querySelector(".ssv-namer")?.remove();
}

function nearbyAssets(f: SiteFrame): { a: AreaAsset; d: number }[] {
  if (!V) return [];
  const lat = f.pos_lat ?? V.site.lat, lon = f.pos_lon ?? V.site.lon;
  const k = Math.cos((lat * Math.PI) / 180);
  return V.assets.filter((a) => a.lat !== undefined && a.lon !== undefined && a.asset_type !== "pipe")
    .map((a) => ({ a, d: Math.hypot((a.lat! - lat) * 111_320, (a.lon! - lon) * 111_320 * k) }))
    .sort((p, q) => p.d - q.d)
    .slice(0, 40);
}

function openNamer(clientX: number, clientY: number): void {
  closeNamer();
  const stage = root?.querySelector<HTMLElement>(".ssv-stage");
  const box = root?.querySelector<HTMLElement>(".ssv-labels");
  const f = V ? visible()[V.i] : undefined;
  const g = imgGeom();
  if (!stage || !box || !V || !f || !g) return;
  const sr = stage.getBoundingClientRect();          // the stage isn't transformed (the layers are)
  const x = (clientX - sr.left - g.ox) / (g.w * g.scale);
  const y = (clientY - sr.top - g.oy) / (g.h * g.scale);
  if (x < 0 || x > 1 || y < 0 || y > 1) return;            // a tap on the letterbox, not the picture
  const pop = el("div", "ssv-namer");
  pop.setAttribute("role", "dialog");
  pop.setAttribute("aria-label", "Mark an item");
  const left = Math.max(8, Math.min(clientX - sr.left + 12, sr.width - 296));
  const top = Math.max(8, Math.min(clientY - sr.top + 12, sr.height - 330));
  pop.style.left = `${left}px`;
  pop.style.top = `${top}px`;
  const marker = el("div", "ssv-name pending");
  marker.style.left = `${g.ox + x * g.w * g.scale}px`;
  marker.style.top = `${g.oy + y * g.h * g.scale}px`;
  marker.append(el("span", "ssv-name-dot"));
  box.append(marker);

  // TYPE first (review #5): which asset it is, or a name, is optional
  let type: string | null = null;
  const types = el("div", "ssv-namer-types");
  types.setAttribute("role", "radiogroup");
  types.setAttribute("aria-label", "What type of item");
  for (const [k, label] of Object.entries(itemTypes)) {
    const b = el("button", "ssv-type", label);
    b.type = "button";
    b.setAttribute("role", "radio");
    b.setAttribute("aria-checked", "false");
    b.addEventListener("click", () => {
      type = type === k ? null : k;
      types.querySelectorAll<HTMLButtonElement>(".ssv-type").forEach((q) => {
        const on = q === b && type === k;
        q.classList.toggle("on", on);
        q.setAttribute("aria-checked", String(on));
      });
    });
    types.append(b);
  }
  const more = el("details", "ssv-namer-more");
  more.append(el("summary", undefined, "Which asset is it? (optional)"));
  const pick = el("select", "ssv-namer-pick");
  pick.append(new Option("Pick a nearby asset…", ""));
  for (const { a, d } of nearbyAssets(f)) pick.append(new Option(`${a.asset_id}${a.asset_type ? ` · ${a.asset_type}` : ""} · ~${Math.round(d)} m`, a.asset_id));
  const text = el("input", "ssv-namer-text");
  text.type = "text";
  text.maxLength = 120;
  text.placeholder = "or type a name, e.g. BFP 41202";
  let source = "tap";
  const row = el("div", "ssv-namer-row");
  row.append(text);
  const SR = (window as unknown as { SpeechRecognition?: new () => SpeechRec; webkitSpeechRecognition?: new () => SpeechRec })
    .SpeechRecognition ?? (window as unknown as { webkitSpeechRecognition?: new () => SpeechRec }).webkitSpeechRecognition;
  if (SR) {
    const mic = el("button", "ssv-namer-mic", "🎤");
    mic.type = "button";
    mic.title = "Say the name";
    mic.setAttribute("aria-label", "Say the name");
    mic.addEventListener("click", () => {
      const rec = new SR();
      rec.lang = "en-NZ";
      rec.interimResults = false;
      rec.onresult = (ev) => {
        text.value = ev.results[0][0].transcript;
        source = "voice";
      };
      mic.classList.add("on");
      rec.onend = () => mic.classList.remove("on");
      rec.start();
    });
    row.append(mic);
  }
  const msg = el("div", "ssv-namer-msg");
  const save = el("button", "ssv-namer-save", "Save");
  const cancel = el("button", "ssv-namer-cancel", "Cancel");
  save.type = cancel.type = "button";
  const done = () => {
    marker.remove();
    pop.remove();
  };
  cancel.addEventListener("click", done);
  save.addEventListener("click", async () => {
    if (!V) return;
    const asset_id = pick.value || null;
    const name = text.value.trim() || null;
    if (!type && !asset_id && !name) {
      msg.textContent = "Say what type it is (or which asset).";
      return;
    }
    const a = asset_id ? V.assets.find((q) => q.asset_id === asset_id) : undefined;
    save.disabled = true;
    const ok = await saveMark(f, { x, y, asset_id, asset_type: type ?? a?.asset_type ?? null, text: name, source });
    if (ok) done();
    else {
      msg.textContent = "Not saved: try again.";
      save.disabled = false;
    }
  });
  const actions = el("div", "ssv-namer-actions");
  actions.append(cancel, save);
  more.append(pick, row);
  pop.append(el("strong", undefined, "What is it?"), types, more, msg, actions);
  stage.append(pop);
  types.querySelector<HTMLButtonElement>(".ssv-type")?.focus();
}

interface SpeechRec {
  lang: string;
  interimResults: boolean;
  onresult: ((ev: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  start(): void;
}

async function removeName(id: number): Promise<void> {
  if (!V) return;
  try {
    const res = await fetch(`${API}/sites/${V.site.id}/labels/${id}`, { method: "DELETE", credentials: "same-origin" });
    if (!res.ok && res.status !== 404) return;
    if (!V) return;
    V.labels = V.labels.filter((l) => l.id !== id);
    renderLabels();
    renderNamingBox();
  } catch {
    /* left as it was */
  }
}

// --- mini-map --------------------------------------------------------------------

const EMPTY: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

function initMini(): void {
  const box = root?.querySelector<HTMLElement>(".ssv-mini");
  if (!box || !V) return;
  const s = V.site;
  try {
    mini = new maplibregl.Map({ container: box, style: MINI_STYLE, center: [s.lon, s.lat], zoom: 18, attributionControl: { compact: true } });
  } catch {
    box.append(el("div", "ssv-empty", "Map unavailable"));
    return;
  }
  mini.on("load", () => {
    if (!mini) return;
    miniReady = true;
    // keep the (compact) attribution collapsed: it would cover the small map
    box.querySelector(".maplibregl-ctrl-attrib")?.classList.remove("maplibregl-compact-show");
    for (const id of ["track", "cone", "here"]) mini.addSource(id, { type: "geojson", data: EMPTY });
    mini.addLayer({ id: "track-all", type: "circle", source: "track", paint: {
      "circle-radius": ["case", ["get", "on"], 3.2, 2.2], "circle-color": ["case", ["get", "asbuilt"], "#dd6b20", "#2b6cb0"],
      "circle-opacity": ["case", ["get", "on"], 0.95, 0.35] } });
    mini.addLayer({ id: "cone-f", type: "fill", source: "cone", paint: { "fill-color": "#3182ce", "fill-opacity": 0.3 } });
    mini.addLayer({ id: "here-c", type: "circle", source: "here", paint: { "circle-radius": 6, "circle-color": "#e53e3e", "circle-stroke-color": "#fff", "circle-stroke-width": 2 } });
    const fc: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: (V?.frames ?? []).filter((f) => f.pos_lat !== null).map((f) => ({
        type: "Feature", geometry: { type: "Point", coordinates: [f.pos_lon as number, f.pos_lat as number] },
        properties: { on: !V?.visit || f.session === V.visit, asbuilt: visitOf(f.session)?.capture_type === "as_built" },
      })),
    };
    (mini.getSource("track") as GeoJSONSource).setData(fc);
    if (s.bbox) mini.fitBounds([[s.bbox[0], s.bbox[1]], [s.bbox[2], s.bbox[3]]], { padding: 30, maxZoom: 19, duration: 0 });
    updateMini();
  });
  mini.on("click", (e) => {
    const r = nearestFrame(visible(), e.lngLat.lng, e.lngLat.lat);
    if (r) jump(r.index);
  });
  mini.getCanvas().style.cursor = "crosshair";
  box.title = "Click to jump to the nearest frame";
}

function updateMini(): void {
  if (!mini || !miniReady || !V) return;
  const f = visible()[V.i];
  const here = mini.getSource("here") as GeoJSONSource | undefined;
  const cone = mini.getSource("cone") as GeoJSONSource | undefined;
  if (!f || f.pos_lat === null || f.pos_lon === null) {
    here?.setData(EMPTY);
    cone?.setData(EMPTY);
    return;
  }
  here?.setData({ type: "Feature", geometry: { type: "Point", coordinates: [f.pos_lon, f.pos_lat] }, properties: {} });
  cone?.setData(f.heading_deg === null ? EMPTY : {
    type: "Feature", geometry: { type: "Polygon", coordinates: [headingCone(f.pos_lat, f.pos_lon, f.heading_deg, f.fov_deg ?? 60)] }, properties: {},
  });
  const b = mini.getBounds();
  if (!b.contains([f.pos_lon, f.pos_lat])) mini.easeTo({ center: [f.pos_lon, f.pos_lat], duration: 300 });
}

function onKey(e: KeyboardEvent): void {
  if (!V) return;
  if (e.key === "Escape") {
    if (root?.querySelector(".ssv-namer")) {
      closeNamer();
      renderLabels();
      return;
    }
    if (panoOpen) {
      closePano();
      return;
    }
    if (V.fs) {
      leaveFullScreen();
      return;
    }
    closeSiteViewer();
    return;
  }
  if (V.tab !== "frames") return;
  if (panoOpen && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
    panBy(e.key === "ArrowLeft" ? 80 : -80);
    e.preventDefault();
    return;
  }
  const t = e.target as HTMLElement | null;
  if (t && (t.tagName === "INPUT" || t.tagName === "SELECT" || t.tagName === "TEXTAREA")) return;
  if (e.key === "ArrowRight" || e.key === "PageDown") go(1);
  else if (e.key === "ArrowLeft" || e.key === "PageUp") go(-1);
  else if (e.key === " ") {
    if (V.playing) stopPlay();
    else startPlay();
  } else if (e.key === "f" || e.key === "F") {
    if (V.fs) leaveFullScreen();
    else enterFullScreen();
  } else if (e.key === "Home") jump(0);
  else if (e.key === "End") jump(visible().length - 1);
  else return;
  e.preventDefault();
}

// --- 3D model --------------------------------------------------------------------

async function renderModel(body: HTMLElement): Promise<void> {
  if (!V) return;
  const siteId = V.site.id;
  const wrap = el("div", "ssv-model");
  body.append(wrap);
  wrap.append(el("div", "ssv-loading", "Checking for a site model…"));
  let st: ModelStatus;
  try {
    st = await getJson<ModelStatus>(`${API}/sites/${siteId}/model`);
  } catch (err) {
    wrap.replaceChildren(el("div", "ssv-error", `Couldn't check the model: ${(err as Error).message}`));
    return;
  }
  if (!V || V.tab !== "model" || V.site.id !== siteId) return;
  if (st.status !== "ready" || !st.model || !st.file_url) {
    const box = el("div", "ssv-model-empty");
    box.append(el("h3", undefined, st.reason ?? "Not built yet: waiting for the scan toolkit."),
      el("p", undefined, "A 3D model of the site (mesh or point cloud, with the recognised assets) appears here once the scan worker has built one. The frames are kept for it."));
    if (st.detail) box.append(el("p", "ssv-muted", st.detail));
    if (st.inputs) {
      box.append(el("p", "ssv-muted", `It will be built from ${st.inputs.current.length} current visit(s); ${st.inputs.history.length} older visit(s) stay as history; ${st.inputs.as_built.length} as-built visit(s) become separate below-ground layers.`));
    }
    wrap.replaceChildren(box);
    return;
  }
  const m = st.model;
  const bar = el("div", "ssv-model-bar");
  const info = [m.sample ? "SAMPLE placeholder model (not built from scans)" : `Revision ${m.revision}`,
    `${m.kind === "mesh" ? "Mesh" : "Point cloud"} (${m.format.toUpperCase()})`, m.built_at ? `built ${shortDate(m.built_at)}` : "",
    georefLabel(m.registration),
    m.fusion === "change_aware" ? "newer visits replace older data" : "current visits only"].filter(Boolean);
  bar.append(el("span", m.sample ? "ssv-badge sample" : "ssv-muted", info.join(" · ")));
  let asBuiltToggle: HTMLInputElement | null = null;
  if ((st.as_built_urls ?? []).length) {
    const lab = el("label", "ssv-toggle");
    asBuiltToggle = el("input");
    asBuiltToggle.type = "checkbox";
    lab.append(asBuiltToggle, el("span", undefined, `Show as-built / below-ground (${st.as_built_urls!.length})`));
    bar.append(lab);
  }
  const canvasBox = el("div", "ssv-canvas");
  const legend = el("div", "ssv-model-legend");
  wrap.replaceChildren(bar, canvasBox, legend);
  let elements: ElementRec[] = [];
  if (st.elements_url) {
    try {
      elements = (await getJson<{ elements: ElementRec[] }>(st.elements_url)).elements;
    } catch {
      /* optional */
    }
  }
  legend.textContent = elements.length
    ? `${elements.length} recognised asset(s): ${elements.map((e) => `${e.kind}${e.match ? ` → ${e.match.asset_id}` : ""}${e.review ? " (moved: flagged for review)" : ""}`).join(", ")}`
    : "The scan toolkit recognised no assets by itself yet (medap-scan-tools has no outdoor detector). What is known about this site:";
  void renderKnown(wrap, siteId);
  try {
    const layers = (st.as_built_urls ?? []).map((u, k) => ({ url: u, format: m.as_built_layers[k]?.format ?? "ply" }));
    threeCleanup = await mountThree(canvasBox, st.file_url, m.format, elements, layers, asBuiltToggle);
  } catch (err) {
    canvasBox.replaceChildren(el("div", "ssv-error", `The 3D viewer couldn't start: ${(err as Error).message}`));
  }
}

// --- what is known about the site (row #43) -----------------------------------------
//
// Connor: "it says we have no labelled asset data which isn't true". The marks people made, and what the
// register holds for the assets a camera had in view and the pipes around the site.

interface Known {
  marks: {
    total: number;
    by_type: Record<string, number>;
    item_types: Record<string, string>;
    by_visit: { session: string; n: number; started_at: string | null; in_model: boolean }[];
    first: { session: string; idx: number } | null;
    ground_truth: boolean;
  };
  assets_in_view: { asset_id: string; asset_type?: string | null; type_label?: string | null; diameter_mm?: number | null; material?: string | null;
    nearest_camera_m?: number | null }[];
  pipes_here: { type_label: string | null; material: string | null; diameter_mm: number | null; water_type: string | null; segments: number }[];
  recognised: number;
  attribution: string[];
}

async function renderKnown(wrap: HTMLElement, siteId: number): Promise<void> {
  let k: Known;
  try {
    k = await getJson<Known>(`${API}/sites/${siteId}/known`);
  } catch {
    return;
  }
  if (!V || V.site.id !== siteId || !wrap.isConnected) return;
  const box = el("div", "ssv-known");
  // the marks
  const m = k.marks;
  const sec1 = el("section");
  sec1.append(el("h4", undefined, `Items marked on the frames (${m.total})`));
  if (m.total) {
    const types = Object.entries(m.by_type).map(([t, n]) => `${n} × ${m.item_types[t] ?? t}`).join(", ");
    const visits = m.by_visit.map((v) => `${v.n} on the ${v.started_at ? shortDate(v.started_at) : "?"} visit${v.in_model ? "" : " (not in this model)"}`).join("; ");
    sec1.append(el("p", undefined, `${types}. ${visits}.`));
    if (m.ground_truth) {
      sec1.append(el("p", "ssv-muted", "This site is a test set: these marks are kept as the answers the automatic placement is " +
        "scored against, so they are never used to place this model."));
    }
    if (m.first) {
      const go = el("button", "ssv-link", "Show them on the frames");
      go.type = "button";
      go.addEventListener("click", () => goToFrame(m.first!.session, m.first!.idx));
      sec1.append(go);
    }
  } else {
    sec1.append(el("p", "ssv-muted", "None yet. On the Frames tab, tap the picture to mark an item by its type."));
  }
  box.append(sec1);
  // the register's assets a placed camera had in view
  const sec2 = el("section");
  sec2.append(el("h4", undefined, `On the register, in view of the cameras (${k.assets_in_view.length})`));
  if (k.assets_in_view.length) {
    const ul = el("ul", "ssv-known-list");
    for (const a of k.assets_in_view) {
      const facts = [a.type_label ?? a.asset_type ?? "asset", a.diameter_mm ? `${Math.round(a.diameter_mm)} mm` : "", a.material ?? ""].filter(Boolean).join(" · ");
      const li = el("li");
      li.setAttribute("data-asset-id", a.asset_id);
      li.append(el("span", undefined, facts), el("small", "ssv-muted", ` ${a.asset_id}${a.nearest_camera_m != null ? ` · ~${Math.round(a.nearest_camera_m)} m from a camera` : ""}`));
      ul.append(li);
    }
    sec2.append(ul);
  } else {
    sec2.append(el("p", "ssv-muted", "None within view of a placed camera."));
  }
  box.append(sec2);
  // the register's pipes around the site
  const sec3 = el("section");
  const nPipes = k.pipes_here.reduce((s, p) => s + p.segments, 0);
  sec3.append(el("h4", undefined, `Pipes on the register around the site (${nPipes} segment${nPipes === 1 ? "" : "s"})`));
  if (k.pipes_here.length) {
    const ul = el("ul", "ssv-known-list");
    for (const p of k.pipes_here.slice(0, 12)) {
      ul.append(el("li", undefined, `${p.segments} × ${[p.type_label ?? "pipe", p.material ?? "material unknown", p.diameter_mm ? `${Math.round(p.diameter_mm)} mm nominal` : "size unknown"].join(" · ")}`));
    }
    sec3.append(ul);
    sec3.append(el("p", "ssv-muted", "Register values: the nominal size as recorded (not an outside or inside diameter), and no valve makes or models are held."));
  }
  box.append(sec3);
  if (k.attribution.length) box.append(el("p", "ssv-muted ssv-attrib", k.attribution.join(" ")));
  wrap.append(box);
}

/* eslint-disable @typescript-eslint/no-explicit-any */
async function mountThree(box: HTMLElement, url: string, format: "glb" | "ply", elements: ElementRec[], asBuilt: { url: string; format: "glb" | "ply" }[],
  toggle: HTMLInputElement | null): Promise<() => void> {
  const T: any = await import(/* @vite-ignore */ `${THREE_BASE}three.module.min.js`);
  const { OrbitControls }: any = await import(/* @vite-ignore */ `${THREE_BASE}OrbitControls.js`);
  const { PLYLoader }: any = await import(/* @vite-ignore */ `${THREE_BASE}PLYLoader.js`);
  const { GLTFLoader }: any = await import(/* @vite-ignore */ `${THREE_BASE}GLTFLoader.js`);
  const w = box.clientWidth || 800;
  const hgt = box.clientHeight || 500;
  const renderer = new T.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  renderer.setSize(w, hgt);
  box.append(renderer.domElement);
  const scene = new T.Scene();
  scene.background = new T.Color(0x1a202c);
  const camera = new T.PerspectiveCamera(55, w / hgt, 0.05, 2000);
  camera.up.set(0, 0, 1); // the scan result frame is z up
  scene.add(new T.AmbientLight(0xffffff, 1.2));
  const sun = new T.DirectionalLight(0xffffff, 1.5);
  sun.position.set(20, -30, 50);
  scene.add(sun);
  const grid = new T.GridHelper(40, 40, 0x4a5568, 0x2d3748);
  grid.rotation.x = Math.PI / 2;
  scene.add(grid);
  scene.add(new T.AxesHelper(2));

  const loadInto = async (u: string, fmt: "glb" | "ply", tint: number | null): Promise<any> => {
    if (fmt === "ply") {
      const geom = await new PLYLoader().loadAsync(u);
      geom.scale(0.001, 0.001, 0.001); // mm -> m
      const hasColour = !!geom.getAttribute("color");
      if (geom.index) {
        geom.computeVertexNormals();
        return new T.Mesh(geom, new T.MeshStandardMaterial({ vertexColors: hasColour && tint === null, color: tint ?? 0xcccccc, side: T.DoubleSide }));
      }
      return new T.Points(geom, new T.PointsMaterial({ size: 0.06, vertexColors: hasColour && tint === null, color: tint ?? 0xffffff }));
    }
    const gltf = await new GLTFLoader().loadAsync(u);
    gltf.scene.rotation.x = Math.PI / 2; // glTF is metres, y up -> the scene's z up
    if (tint !== null) gltf.scene.traverse((o: any) => { if (o.isMesh) o.material = new T.MeshStandardMaterial({ color: tint }); });
    return gltf.scene;
  };

  const model = await loadInto(url, format, null);
  scene.add(model);
  // recognised assets: a marker per element (red when it moved and is flagged)
  for (const e of elements) {
    const s = new T.Mesh(new T.SphereGeometry(0.18, 16, 12), new T.MeshStandardMaterial({ color: e.review ? 0xe53e3e : 0xf6e05e }));
    s.position.set(e.centre_mm[0] / 1000, e.centre_mm[1] / 1000, e.centre_mm[2] / 1000 + 0.2);
    scene.add(s);
  }
  const bbox = new T.Box3().setFromObject(model);
  const centre = bbox.getCenter(new T.Vector3());
  const size = bbox.getSize(new T.Vector3()).length() || 10;
  camera.position.set(centre.x + size * 0.35, centre.y - size * 0.5, centre.z + size * 0.35);
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.target.copy(centre);
  controls.update();

  const layers: any[] = [];
  toggle?.addEventListener("change", () => void (async () => {
    if (toggle.checked && !layers.length) {
      for (const a of asBuilt) {
        try {
          layers.push(await loadInto(a.url, a.format, 0xdd6b20));
        } catch {
          /* skip a broken layer */
        }
      }
    }
    for (const l of layers) {
      if (toggle.checked) scene.add(l);
      else scene.remove(l);
    }
  })());

  let raf = 0;
  const loop = () => {
    raf = requestAnimationFrame(loop);
    controls.update();
    renderer.render(scene, camera);
  };
  loop();
  const ro = new ResizeObserver(() => {
    const W = box.clientWidth || w;
    const H = box.clientHeight || hgt;
    renderer.setSize(W, H);
    camera.aspect = W / H;
    camera.updateProjectionMatrix();
  });
  ro.observe(box);
  return () => {
    cancelAnimationFrame(raf);
    ro.disconnect();
    controls.dispose();
    renderer.dispose();
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/** What the model's georeference rests on. A walk-only fit's residual is how well the scan's path agrees with the
 * phone's own GPS track - not how close the model is to the register (a GPS bias shared by the walk is invisible to
 * it), so it is never shown as accuracy. */
function georefLabel(r: { residual_m: number | null; n_pairs: number; basis?: "walk" | "assets" | null; n_asset_pairs?: number | null } | null): string {
  if (!r) return "not georeferenced";
  if (r.basis === "walk")
    return `placed by phone GPS only (no assets matched; the walk agrees with the scan to ${r.residual_m ?? "?"} m - GPS error not measured)`;
  if (r.basis === "assets")
    return `georeferenced to ${r.n_asset_pairs ?? "?"} matched assets: residual ${r.residual_m ?? "?"} m`;
  return `georeferenced: residual ${r.residual_m ?? "?"} m from ${r.n_pairs} pairs`;
}
